import { useState, useMemo, useEffect, useRef } from "react";
import { C } from "./theme/tokens";
import GlobalStyle from "./theme/GlobalStyle";
import { dict, LANGS } from "./i18n/dict";
import { useBreakpoint } from "./hooks/useBreakpoint";
import { useAuth } from "./hooks/useAuth";
import { useActiveSignal } from "./hooks/useActiveSignal";
import { useActiveScan } from "./hooks/useActiveScan";
import { getSectionAccess } from "./access/sectionAccess";
import { AmbientBackground } from "./components/common/AmbientBackground";
import { TopBar, NavDrawer } from "./components/common/Navigation";
import { AuthLoadingScreen } from "./components/auth/AuthLoadingScreen";
import { BrowserAuthScreen } from "./components/auth/BrowserAuthScreen";
import { RegistrationScreen, RegistrationClosedScreen } from "./components/auth/RegistrationScreen";
import { ActiveSignalOverlay } from "./screens/signals/ActiveSignalOverlay";
import { AccountRestrictedOverlay } from "./components/common/AccountRestrictedOverlay";
import { MaintenanceScreen } from "./components/auth/MaintenanceScreen";
import { Screens } from "./Screens";

export default function App() {
  const [tab, setTab] = useState("profile");
  const [menuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useState("ru");
  const t = dict[lang];
  const { isWide } = useBreakpoint();
  const auth = useAuth();
  const activeSignal = useActiveSignal(auth.authFetch);
  const activeScan = useActiveScan(auth.authFetch);

  // Telegram Mini App bootstrap: tell the client we're ready to be shown,
  // then expand to full height so the WebView isn't stuck in its
  // half-height "peek" state.
  useEffect(() => {
    const tg = window?.Telegram?.WebApp;
    if (!tg) return;
    try {
      tg.ready?.();
      tg.expand?.();
    } catch (e) {}
  }, []);

  // While a signal (Signals bot or Scanner) is running, ask Telegram to
  // confirm before the user can swipe/close the Mini App — otherwise
  // closing mid-trade silently discards the open position instead of
  // letting the countdown/result flow finish.
  useEffect(() => {
    const tg = window?.Telegram?.WebApp;
    if (!tg) return;
    try {
      if (activeSignal.entry || activeScan.entry) {
        tg.enableClosingConfirmation?.();
      } else {
        tg.disableClosingConfirmation?.();
      }
    } catch (e) {}
  }, [activeSignal.entry, activeScan.entry]);

  // Seed the UI language from the backend's user.language the first time
  // we get a user back (login or registration). After that the user is
  // free to switch languages by hand via the profile screen without
  // getting overridden again.
  const langSyncedRef = useRef(false);
  useEffect(() => {
    if (langSyncedRef.current) return;
    const backendLang = auth.user?.language;
    if (backendLang && LANGS.includes(backendLang)) {
      setLang(backendLang);
      langSyncedRef.current = true;
    } else if (auth.user) {
      // user is loaded but has no recognized language yet — stop trying
      langSyncedRef.current = true;
    }
  }, [auth.user]);

  useEffect(() => {
    if (tab === "profile" && auth.state === "ready") {
      auth.refetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, auth.state]);

  // Wraps setLang: update the UI instantly (snappy), then persist to the
  // backend in the background so the choice survives reopening the app.
  // Only persists once auth is actually ready — no point PATCHing before
  // there's a full-scoped token to send.
  const handleLangChange = (l) => {
    setLang(l);
    if (auth.state === "ready") auth.updateLanguage(l);
  };

  // Selecting a destination from the drawer both switches tabs and
  // closes the drawer — the mockup's nav items collapse the menu on
  // tap rather than leaving it open over the new screen.
  const handleNavSelect = (key) => {
    setTab(key);
    setMenuOpen(false);
  };

  const { sections: accessibleSections, lockReason } = useMemo(
    () => getSectionAccess(auth.user),
    [auth.user]
  );
  const maintenanceMode = auth.state === "ready"
    ? auth.user?.is_admin !== true && !auth.user?.accessible_sections?.includes("is_maintenance_mode")
    : auth.maintenanceMode;

  // Ambient background: two soft radial glows — sage top-left, gold
  // bottom-right — fixed behind every screen so the two brand colors
  // blend evenly across the whole app instead of only appearing inside
  // individual cards.
  const ambientBg = {
    backgroundImage: `radial-gradient(circle at 8% -6%, ${C.green}26, transparent 42%), radial-gradient(circle at 96% 108%, ${C.violet}22, transparent 45%)`,
    backgroundColor: C.canvas,
    backgroundAttachment: "fixed",
  };

  const renderContent = (wide) => {
    if (!auth.registrationChecked) return <AuthLoadingScreen t={t} />;
    if (maintenanceMode) {
      return (
        <MaintenanceScreen
          t={t}
          endsAt={auth.user?.maintenance_ends_at || auth.maintenanceEndsAt}
          durationSeconds={auth.user?.maintenance_duration_seconds || auth.maintenanceDurationSeconds}
        />
      );
    }
    if (!auth.registrationOpen && auth.user?.is_admin !== true) {
      return <RegistrationClosedScreen t={t} wide={wide} />;
    }
    if (auth.state === "loading") return <AuthLoadingScreen t={t} />;
    if (auth.state === "fatal") return <AuthLoadingScreen t={t} />; // або окремий error-screen
    if (auth.state === "blocked") return <AuthLoadingScreen t={t} />; // блок-екран рендериться нижче окремим overlay
    if (auth.state === "register_closed") {
      return <RegistrationClosedScreen t={t} wide={wide} maintenanceMode={auth.maintenanceMode} />;
    }
    if (auth.state === "browser_auth") {
      return (
        <BrowserAuthScreen
          t={t}
          error={auth.error}
          authBusy={auth.authBusy}
          onLogin={auth.loginEmail}
          onSignup={auth.signupEmail}
          onSubmitTrial={auth.submitTrial}
          registrationOpen={auth.registrationOpen}
          maintenanceMode={auth.maintenanceMode}
          wide={wide}
        />
      );
    }
    if (auth.state === "register" || auth.state === "linking") {
      return (
        <RegistrationScreen
          t={t}
          authState={auth.state}
          error={auth.error}
          onSubmit={auth.submitAccountId}
          onSubmitTrial={auth.submitTrial}
          maintenanceMode={auth.maintenanceMode}
          wide={wide}
        />
      );
    }
    return (
      <Screens
        tab={tab}
        setTab={setTab}
        t={t}
        lang={lang}
        setLang={handleLangChange}
        wide={wide}
        user={auth.user}
        stats={auth.stats}
        authFetch={auth.authFetch}
        token={auth.token}
        accessibleSections={accessibleSections}
        lockReason={lockReason}
        activeSignal={activeSignal}
        activeScan={activeScan}
        requestTelegramLinkCode={auth.requestTelegramLinkCode}
        setPassword={auth.setPassword}
        linkEmail={auth.linkEmail}
        onLogout={auth.logout}
        trialRenewalStatus={auth.trialRenewalStatus}
        trialRenewalError={auth.trialRenewalError}
        submitTrialRenewal={auth.submitTrialRenewal}
        resetTrialRenewal={auth.resetTrialRenewal}
      />
    );
  };

  // The active-signal / active-scan overlays are rendered once here,
  // as siblings of the wide/narrow layout switch below rather than
  // nested inside either SignalsScreen or ScannerScreen. That's what
  // keeps them on screen when the viewport crosses the wide/narrow
  // breakpoint mid-signal — resizing swaps which of the two branches
  // below is mounted, which would otherwise unmount anything living
  // inside them. Only one of the two should ever be active at once
  // (each bot's overlay blocks its own screen while running), but they
  // don't share state so both are checked independently here.
  const activeSignalOverlay =
    auth.state === "ready" && activeSignal.entry && activeSignal.overlayOpen !== false ? (
      <ActiveSignalOverlay
        signal={activeSignal.entry.signal}
        requestedPair={activeSignal.entry.requestedPair}
        startedAt={activeSignal.entry.startedAt}
        t={t}
        onSubmitResult={activeSignal.submitResult}
        onClose={activeSignal.closeOverlay}
        onCancel={activeSignal.cancelSignal}
        submitting={activeSignal.resultSubmitting}
        resultError={activeSignal.resultError}
      />
    ) : null;

  const activeScanOverlay =
    auth.state === "ready" && activeScan.entry ? (
      <ActiveSignalOverlay
        signal={activeScan.entry.signal}
        titleLabel={activeScan.titleLabel || t.scan_result_title}
        startedAt={activeScan.entry.startedAt}
        t={t}
        onSubmitResult={activeScan.submitResult}
        onCancel={activeScan.cancelScan}
        submitting={activeScan.resultSubmitting}
        resultError={activeScan.resultError}
      />
    ) : null;

  // Backend-issued lockout: user.status === false means the account was
  // restricted by an admin. Rendered above everything else (including
  // the active-signal/scan overlays) and with no dismiss control at
  // all, per product requirement — the user can only leave via support.
  const accountRestrictedOverlay =
    auth.state === "blocked" || (auth.state === "ready" && auth.user?.status === false) ? (
      <AccountRestrictedOverlay t={t} />
    ) : null;

  // Drawer is shared by both breakpoints below — mounted once here
  // so App only renders one instance regardless of which branch is
  // active (resizing across the breakpoint just swaps which return
  // block is mounted, same as before with the old Sidebar/BottomNav).
  const navDrawer =
    auth.state === "ready" ? (
      <NavDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        tab={tab}
        setTab={handleNavSelect}
        t={t}
        accessibleSections={accessibleSections}
      />
    ) : null;

  if (!isWide) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          paddingTop: "var(--tg-content-safe-area-inset-top, var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px)))",
          paddingBottom: "var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))",
          ...ambientBg,
        }}
      >
        <GlobalStyle />
        <AmbientBackground />
        <div
          style={{
            width: "100%",
            maxWidth: 420,
            minHeight: "calc(100vh - var(--tg-content-safe-area-inset-top, var(--tg-safe-area-inset-top, env(safe-area-inset-top, 0px))) - var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))",
            display: "flex",
            flexDirection: "column",
            fontFamily: "'Space Grotesk', sans-serif",
          }}
        >
          {auth.state === "ready" && <TopBar onOpenMenu={() => setMenuOpen(true)} t={t} />}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {renderContent(false)}
          </div>
        </div>
        {navDrawer}
        {activeSignalOverlay}
        {activeScanOverlay}
        {accountRestrictedOverlay}
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Space Grotesk', sans-serif",
        ...ambientBg,
      }}
    >
      <GlobalStyle />
      <AmbientBackground />
      {auth.state === "ready" && <TopBar onOpenMenu={() => setMenuOpen(true)} t={t} />}
      <div style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
        {/* Live Charts wants the chart edge-to-edge, so it skips the usual
            centered/padded content frame that every other tab still uses. */}
        <div
          style={
            tab === "live"
              ? { minHeight: "100vh" }
              : { maxWidth: 1180, margin: "0 auto", padding: "20px 48px 48px", minHeight: "100vh" }
          }
        >
          {renderContent(true)}
        </div>
      </div>
      {navDrawer}
      {activeSignalOverlay}
      {activeScanOverlay}
      {accountRestrictedOverlay}
    </div>
  );
}